import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export const CartContext = createContext();

// useCart 훅 export (다른 컴포넌트가 useContext 대신 사용 가능)
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const CART_STORAGE_KEY = 'cart';

// 장바구니 라인 아이템 식별 키
// 같은 화환이라도 근조 리본 문구(좌/우/보내는분)가 다르면 별도 라인으로 취급한다.
const getLineKey = (item) => {
  const ribbon = item.ribbon || {};
  return [
    item.id,
    ribbon.left || '',
    ribbon.right || '',
    ribbon.sender || '',
  ].join('|');
};

// 로컬 스토리지에서 장바구니를 동기적으로 복원 (B7: JSON.parse 손상 데이터 방어)
// useState lazy initializer 로 마운트 시점에 즉시 복원하여,
// 복원 effect 와 저장 effect 사이의 race(빈 배열로 덮어쓰기, StrictMode 2회 실행 포함)를 원천 차단한다.
const loadInitialCart = () => {
  try {
    const storedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (!storedCart) return [];
    const parsed = JSON.parse(storedCart);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('장바구니 복원 실패 - 손상된 데이터로 초기화합니다:', error);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (_) {
      /* noop */
    }
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(loadInitialCart);
  const [totalPrice, setTotalPrice] = useState(0);

  // 장바구니 변경시 로컬 스토리지 업데이트 및 총액 계산
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.error('장바구니 저장 실패:', error);
    }
    const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    setTotalPrice(total);
  }, [cartItems]);

  // 탭 간 동기화: 다른 탭에서 장바구니가 변경되면 현재 탭에도 반영
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key !== CART_STORAGE_KEY) return;
      try {
        const parsed = e.newValue ? JSON.parse(e.newValue) : [];
        setCartItems(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error('탭 간 장바구니 동기화 실패:', error);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // 장바구니에 상품 추가 (리본 옵션 포함 가능)
  const addToCart = useCallback((wreath, quantity = 1, options = {}) => {
    const ribbon = options.ribbon || null;
    const newItem = { ...wreath, quantity, ribbon };
    const newKey = getLineKey(newItem);

    setCartItems((prevItems) => {
      const existingIndex = prevItems.findIndex((item) => getLineKey(item) === newKey);

      if (existingIndex >= 0) {
        // 동일 상품 + 동일 리본 옵션이면 수량만 증가
        return prevItems.map((item, idx) =>
          idx === existingIndex ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [...prevItems, newItem];
    });
  }, []);

  // 장바구니에서 상품 제거 (id 또는 lineKey 모두 지원)
  const removeFromCart = useCallback((identifier) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.id !== identifier && getLineKey(item) !== identifier)
    );
  }, []);

  // 여러 라인 일괄 삭제 (lineKey 배열)
  const removeMany = useCallback((keys = []) => {
    const keySet = new Set(keys);
    setCartItems((prevItems) => prevItems.filter((item) => !keySet.has(getLineKey(item))));
  }, []);

  // 상품 수량 변경 (id 또는 lineKey)
  const updateQuantity = useCallback(
    (identifier, quantity) => {
      if (quantity < 1) {
        removeFromCart(identifier);
        return;
      }
      setCartItems((prevItems) =>
        prevItems.map((item) =>
          item.id === identifier || getLineKey(item) === identifier
            ? { ...item, quantity }
            : item
        )
      );
    },
    [removeFromCart]
  );

  // 장바구니 비우기
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // 라인 전체 복원 (Undo 용도)
  const restoreItems = useCallback((items = []) => {
    if (!Array.isArray(items) || items.length === 0) return;
    setCartItems((prevItems) => {
      const existingKeys = new Set(prevItems.map(getLineKey));
      const toRestore = items.filter((it) => !existingKeys.has(getLineKey(it)));
      return [...prevItems, ...toRestore];
    });
  }, []);

  // 상품 개수 계산
  const getTotalItems = useCallback(() => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  }, [cartItems]);

  // 장바구니에 상품이 있는지 확인 (id 기준)
  const isInCart = useCallback(
    (wreathId) => {
      return cartItems.some((item) => item.id === wreathId);
    },
    [cartItems]
  );

  const value = {
    cartItems,
    totalPrice,
    addToCart,
    removeFromCart,
    removeMany,
    updateQuantity,
    clearCart,
    restoreItems,
    getTotalItems,
    isInCart,
    getLineKey,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
